/**
 * Phone Verification Page
 * Allows users to add/update their phone number with SMS verification
 * Handles the auth/provider-already-linked error properly
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PhoneAuthProvider,
  linkWithCredential,
  updatePhoneNumber,
  RecaptchaVerifier,
} from 'firebase/auth';
import { auth } from '../services/firebase';
import { Phone, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

type Step = 'phone' | 'code';

export default function PhoneVerificationPage(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  // Check if user already has phone linked
  const hasPhoneLinked = auth.currentUser?.providerData.some(
    (provider) => provider.providerId === 'phone'
  );

  // Initialize reCAPTCHA verifier
  useEffect(() => {
    if (!recaptchaContainerRef.current) return;

    // Clean up existing verifier
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
    }

    recaptchaVerifierRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
      size: 'invisible',
      callback: () => {
        // reCAPTCHA solved
      },
      'expired-callback': () => {
        setError('reCAPTCHA expired. Please try again.');
      },
    });

    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  // Format phone number for display
  const formatPhoneDisplay = (phone: string): string => {
    // Simple formatting - can be enhanced with a library
    return phone;
  };

  // Send verification code
  const handleSendCode = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!auth.currentUser) {
        throw new Error('You must be logged in to verify your phone number');
      }

      if (!recaptchaVerifierRef.current) {
        throw new Error('reCAPTCHA not initialized. Please refresh the page.');
      }

      // Format phone number (ensure it has country code)
      let formattedPhone = phoneNumber.trim();
      if (!formattedPhone.startsWith('+')) {
        // Default to UK if no country code provided
        formattedPhone = '+44' + formattedPhone.replace(/^0/, '');
      }

      // Use PhoneAuthProvider to get verification ID for linking
      const provider = new PhoneAuthProvider(auth);
      const verId = await provider.verifyPhoneNumber(
        formattedPhone,
        recaptchaVerifierRef.current
      );

      setVerificationId(verId);
      setPhoneNumber(formattedPhone);
      setStep('code');
    } catch (err: any) {
      console.error('Error sending verification code:', err);
      handleFirebaseError(err);

      // Reset reCAPTCHA on error
      if (recaptchaVerifierRef.current && recaptchaContainerRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = new RecaptchaVerifier(
          auth,
          recaptchaContainerRef.current,
          { size: 'invisible' }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Verify the code and link/update phone
  const handleVerifyCode = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!auth.currentUser) {
        throw new Error('You must be logged in to verify your phone number');
      }

      if (!verificationId) {
        throw new Error('Verification session expired. Please start over.');
      }

      // Create credential from verification ID and code
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);

      // Check if phone is already linked
      const isPhoneLinked = auth.currentUser.providerData.some(
        (provider) => provider.providerId === 'phone'
      );

      if (isPhoneLinked) {
        // Phone already linked - update to new number
        await updatePhoneNumber(auth.currentUser, credential);
      } else {
        // No phone linked yet - link the credential
        await linkWithCredential(auth.currentUser, credential);
      }

      // Success - navigate back to user page
      navigate('/user?tab=overview', {
        state: { message: 'Phone number verified successfully!' }
      });
    } catch (err: any) {
      console.error('Error verifying code:', err);
      handleFirebaseError(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Firebase errors with user-friendly messages
  const handleFirebaseError = (err: any): void => {
    const errorCode = err.code || '';

    switch (errorCode) {
      case 'auth/provider-already-linked':
        // This means the SAME phone number is already linked
        setError(
          'This phone number is already linked to your account. ' +
          'If you want to change it, please use a different number.'
        );
        break;
      case 'auth/credential-already-in-use':
        setError(
          'This phone number is already associated with another account. ' +
          'Please use a different phone number.'
        );
        break;
      case 'auth/invalid-verification-code':
        setError('Invalid verification code. Please check and try again.');
        break;
      case 'auth/code-expired':
        setError('Verification code has expired. Please request a new one.');
        setStep('phone');
        break;
      case 'auth/too-many-requests':
        setError('Too many attempts. Please wait a few minutes before trying again.');
        break;
      case 'auth/invalid-phone-number':
        setError('Invalid phone number format. Please include country code (e.g., +44).');
        break;
      case 'auth/missing-phone-number':
        setError('Please enter a phone number.');
        break;
      case 'auth/quota-exceeded':
        setError('SMS quota exceeded. Please try again later.');
        break;
      case 'auth/captcha-check-failed':
        setError('reCAPTCHA verification failed. Please refresh and try again.');
        break;
      default:
        setError(err.message || 'An error occurred. Please try again.');
    }
  };

  // Handle code input with auto-submit
  const handleCodeChange = (value: string): void => {
    // Only allow digits
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(digits);
  };

  // Go back to phone input
  const handleChangeNumber = (): void => {
    setStep('phone');
    setVerificationCode('');
    setVerificationId(null);
    setError('');
  };

  return (
    <div className="container-custom py-12">
      <div className="max-w-md mx-auto">
        <div className="card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone size={32} className="text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {step === 'phone' ? 'Verify Phone Number' : 'Enter Verification Code'}
            </h1>
            {step === 'code' && (
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                We sent a 6-digit code to{' '}
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {formatPhoneDisplay(phoneNumber)}
                </span>
              </p>
            )}
            {hasPhoneLinked && step === 'phone' && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                You already have a phone number linked. Adding a new one will replace it.
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Phone Input Step */}
          {step === 'phone' && (
            <form onSubmit={handleSendCode} className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+44 7123 456789"
                  required
                  className="input"
                  autoFocus
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Include country code (e.g., +44 for UK, +1 for US)
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || !phoneNumber.trim()}
                className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending Code...
                  </>
                ) : (
                  'Send Verification Code'
                )}
              </button>
            </form>
          )}

          {/* Code Input Step */}
          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-6">
              <div>
                <label htmlFor="code" className="block text-sm font-medium mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  id="code"
                  value={verificationCode}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  required
                  className="input text-center text-2xl tracking-[0.5em] font-mono"
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>

              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Verify Code
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleChangeNumber}
                className="w-full btn btn-secondary flex items-center justify-center gap-2"
              >
                <ArrowLeft size={18} />
                Change Phone Number
              </button>
            </form>
          )}

          {/* reCAPTCHA container (invisible) */}
          <div ref={recaptchaContainerRef} id="recaptcha-container" />

          {/* Back to account link */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/user?tab=overview')}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              Back to My Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
